// frontend/src/pages/PoliticaReembolso.jsx
import { useState } from "react";
import { ArrowLeft, Download } from "lucide-react";

export default function PoliticaReembolso() {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const pdfUrl = `${process.env.PUBLIC_URL}/pdfs/politica-reembolso.pdf`;

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'politica-reembolso.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsGeneratingPDF(false);
  };

  return (
    <>
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; background: white; }
            .no-print { display: none !important; }
            .print-content { margin: 0; padding: 0.5cm; width: 100%; }
            .print-content h1 { font-size: 18pt; margin-bottom: 10pt; }
            .print-content h2 { font-size: 14pt; margin-top: 15pt; margin-bottom: 8pt; page-break-after: avoid; }
            .print-content p { font-size: 10pt; line-height: 1.4; margin-bottom: 6pt; page-break-inside: avoid; }
            .print-content ul { margin: 0 0 8pt 0; padding-left: 20pt; }
            .print-content li { font-size: 10pt; margin-bottom: 3pt; page-break-inside: avoid; }
            .print-content .section { page-break-inside: avoid; margin-bottom: 12pt; }
            .print-content .border-b { border-bottom: 0.5pt solid #ccc; }
            @page { size: A4; margin: 1.5cm; }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-50 pt-2 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                  alt="Brechó Vizinho"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Política de Reembolso</h1>
                  <p className="text-xs text-gray-500">Versão 1.0 | 15/06/2026</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => window.history.back()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-[#449879] text-white rounded-lg hover:bg-[#2d5f4d] transition-colors disabled:opacity-50"
                >
                  {isGeneratingPDF ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Baixar PDF</span>
                </button>
              </div>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 print:shadow-none print:p-0 print:bg-white">
            <div className="print-content prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800">
              
              {/* TÍTULO */}
              <div className="text-center mb-8 print:mb-4">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">POLÍTICA DE REEMBOLSO</h1>
                <p className="text-sm text-gray-500">Versão 1.0 | Data de Vigência: 15/06/2026</p>
                <div className="w-20 h-1 bg-[#449879] mx-auto mt-3 rounded-full print:bg-gray-400" />
              </div>

              {/* INTRODUÇÃO */}
              <p className="text-gray-600 leading-relaxed mb-4">
                Esta Política estabelece as condições aplicáveis aos pagamentos realizados pelos usuários para contratação de 
                recursos pagos disponibilizados pelo Brechó Vizinho.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Ao adquirir qualquer recurso pago da plataforma, o usuário declara estar ciente e de acordo com as condições 
                descritas neste documento.
              </p>

              {/* 1. OBJETIVO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">1. OBJETIVO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Os recursos pagos oferecidos pelo Brechó Vizinho têm como finalidade ampliar a visibilidade de anúncios, 
                  promover conteúdos ou disponibilizar funcionalidades adicionais dentro da plataforma.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os serviços comercializados possuem natureza digital.
                </p>
              </div>

              {/* 2. RECURSOS PAGOS ABRANGIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">2. RECURSOS PAGOS ABRANGIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política aplica-se a todos os recursos pagos disponibilizados pela plataforma, incluindo:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Pacotes de anúncios;</li>
                  <li>Anúncios em destaque;</li>
                  <li>Espaços publicitários;</li>
                  <li>Patrocínios;</li>
                  <li>Funcionalidades premium;</li>
                  <li>Outros serviços digitais que venham a ser disponibilizados.</li>
                </ul>
              </div>

              {/* 3. RESULTADOS NÃO GARANTIDOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">3. RESULTADOS NÃO GARANTIDOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A contratação de qualquer recurso pago não garante:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Vendas;</li>
                  <li>Doações;</li>
                  <li>Contatos;</li>
                  <li>Visualizações mínimas;</li>
                  <li>Leads;</li>
                  <li>Retorno financeiro;</li>
                  <li>Resultado comercial específico.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  Os resultados dependem de fatores externos que não estão sob controle do Brechó Vizinho.
                </p>
              </div>

              {/* 4. REEMBOLSO POR ARREPENDIMENTO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">4. REEMBOLSO POR ARREPENDIMENTO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Quando permitido pela legislação aplicável, o usuário poderá solicitar cancelamento e reembolso caso o 
                  recurso adquirido ainda não tenha sido utilizado ou ativado.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Após a utilização parcial ou total do recurso contratado, o reembolso poderá não ser aplicável.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Cada solicitação será analisada individualmente.
                </p>
              </div>

              {/* 5. REEMBOLSO POR FALHA TÉCNICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">5. REEMBOLSO POR FALHA TÉCNICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O usuário poderá solicitar reembolso quando houver falha técnica comprovadamente atribuível à plataforma 
                  que impeça a utilização do serviço contratado.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Exemplos:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Destaque não ativado;</li>
                  <li>Pacote não creditado;</li>
                  <li>Cobrança duplicada;</li>
                  <li>Erro de processamento da compra.</li>
                </ul>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Nessas situações, o Brechó Vizinho poderá optar por:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Corrigir a falha;</li>
                  <li>Conceder crédito equivalente;</li>
                  <li>Realizar reembolso parcial;</li>
                  <li>Realizar reembolso integral.</li>
                </ul>
              </div>

              {/* 6. SITUAÇÕES NÃO ELEGÍVEIS PARA REEMBOLSO */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">6. SITUAÇÕES NÃO ELEGÍVEIS PARA REEMBOLSO</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Não haverá reembolso quando:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Usuário não obtiver vendas;</li>
                  <li>Usuário não obtiver contatos;</li>
                  <li>Anúncio permanecer sem interesse de terceiros;</li>
                  <li>Usuário desistir após utilizar o serviço;</li>
                  <li>Recurso contratado tiver sido efetivamente entregue;</li>
                  <li>A solicitação decorrer de erro de avaliação comercial do usuário.</li>
                </ul>
              </div>

              {/* 7. PACOTES DE ANÚNCIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">7. PACOTES DE ANÚNCIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Pacotes de anúncios utilizados total ou parcialmente não são reembolsáveis.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Pacotes não utilizados poderão ser analisados conforme a legislação aplicável e as circunstâncias do caso.
                </p>
              </div>

              {/* 8. ANÚNCIOS EM DESTAQUE */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">8. ANÚNCIOS EM DESTAQUE</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Uma vez iniciado o período de destaque contratado, o serviço será considerado prestado.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O simples fato de não gerar vendas ou contatos não constitui motivo para reembolso.
                </p>
              </div>

              {/* 9. PUBLICIDADE E PATROCÍNIOS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">9. PUBLICIDADE E PATROCÍNIOS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Campanhas publicitárias iniciadas ou veiculadas não são reembolsáveis.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Caso ocorra falha operacional que impeça a exibição da campanha contratada, o Brechó Vizinho poderá:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-2 text-gray-600 ml-4">
                  <li>Prorrogar o período contratado;</li>
                  <li>Conceder crédito equivalente;</li>
                  <li>Avaliar eventual reembolso.</li>
                </ul>
              </div>

              {/* 10. PRAZO PARA SOLICITAÇÕES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">10. PRAZO PARA SOLICITAÇÕES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Solicitações relacionadas a cobranças, pagamentos ou utilização de serviços deverão ser realizadas em prazo 
                  razoável após a identificação do problema.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O usuário deverá apresentar informações suficientes para análise da solicitação.
                </p>
              </div>

              {/* 11. ANÁLISE DAS SOLICITAÇÕES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">11. ANÁLISE DAS SOLICITAÇÕES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Todas as solicitações serão analisadas individualmente.
                </p>
                <p className="text-gray-600 leading-relaxed mb-2">
                  O Brechó Vizinho poderá solicitar informações complementares para verificação dos fatos.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A decisão será comunicada ao usuário pelos canais oficiais da plataforma.
                </p>
              </div>

              {/* 12. PREVENÇÃO DE FRAUDES */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">12. PREVENÇÃO DE FRAUDES</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Solicitações abusivas, fraudulentas ou realizadas de má-fé poderão ser recusadas.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O Brechó Vizinho poderá adotar medidas adicionais para proteger a integridade da plataforma.
                </p>
              </div>

              {/* 13. ALTERAÇÕES DESTA POLÍTICA */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">13. ALTERAÇÕES DESTA POLÍTICA</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Esta Política poderá ser atualizada periodicamente para refletir alterações legais, operacionais ou comerciais.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  A versão mais recente estará sempre disponível na plataforma.
                </p>
              </div>

              {/* 14. DISPOSIÇÕES FINAIS */}
              <div className="section mb-6">
                <h2 className="text-lg font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">14. DISPOSIÇÕES FINAIS</h2>
                <p className="text-gray-600 leading-relaxed mb-2">
                  A aquisição de qualquer recurso pago implica concordância integral com esta Política de Reembolso.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Os casos não previstos serão analisados individualmente, observando a legislação aplicável e os princípios de 
                  boa-fé e razoabilidade.
                </p>
              </div>

              {/* ASSINATURA FINAL */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Última atualização: 15/06/2026<br />
                  Versão: 1.0<br />
                  Brechó Vizinho<br />
                  E-mail: <span className="text-[#449879] font-medium">info@brechovizinho.com.br</span>
                </p>
              </div>
            </div>
          </div>

          {/* Botão voltar ao topo */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="no-print fixed bottom-20 right-4 bg-[#449879] text-white p-3 rounded-full shadow-lg hover:bg-[#2d5f4d] transition-colors z-40"
            aria-label="Voltar ao topo"
          >
            ↑
          </button>
        </div>
      </div>
    </>
  );
}